import { Injectable, inject, signal } from '@angular/core';
import { TranslocoService } from '@jsverse/transloco';
import type { User } from '@millim/shared-types';

export const SUPPORTED_LOCALES = ['en', 'fr'] as const;
export type Locale = (typeof SUPPORTED_LOCALES)[number];

const STORAGE_KEY = 'locale';

const isSupported = (value: unknown): value is Locale =>
  typeof value === 'string' && (SUPPORTED_LOCALES as readonly string[]).includes(value);

@Injectable({ providedIn: 'root' })
export class LocaleService {
  private readonly transloco = inject(TranslocoService);
  private readonly _locale = signal<Locale>('en');
  readonly locale = this._locale.asReadonly();

  /**
   * Resolves the locale to use at app bootstrap, before the first paint.
   * Order: localStorage → navigator.language → 'en'.
   * (Server profile is layered on top later via syncFromProfile.)
   */
  resolveInitialLocale(): Locale {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (isSupported(stored)) return stored;
    if (typeof navigator !== 'undefined' && navigator.language?.toLowerCase().startsWith('fr')) {
      return 'fr';
    }
    return 'en';
  }

  /** Set + persist + propagate to Transloco. Used by the switcher and profile form. */
  set(locale: Locale): void {
    if (!isSupported(locale)) {
      throw new Error(`Unsupported locale: ${locale}`);
    }
    this._locale.set(locale);
    localStorage.setItem(STORAGE_KEY, locale);
    this.transloco.setActiveLang(locale);
  }

  /**
   * Initialize the locale at bootstrap WITHOUT persisting to localStorage.
   * Persistence happens later when the user explicitly chooses a locale.
   */
  initialize(locale: Locale): void {
    if (!isSupported(locale)) {
      throw new Error(`Unsupported locale: ${locale}`);
    }
    this._locale.set(locale);
    this.transloco.setActiveLang(locale);
  }

  /**
   * Reconcile with the server profile after login / profile fetch.
   * Returns 'needs-write' when the user record has no (or unsupported) locale —
   * caller should PATCH the currently-active locale back to the server.
   */
  syncFromProfile(user: User): 'synced' | 'needs-write' {
    if (isSupported(user.locale)) {
      this.set(user.locale);
      return 'synced';
    }
    return 'needs-write';
  }
}
